import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import CodeInput from './widgets/CodeInput';
import '../styles/MfaCodeEntry.css';

const MfaCodeEntry = ({ userId, reason, triggerReason, deviceFingerprint, onSuccess, onCancel }) => {
  const [code, setCode] = useState('');
  const [codeId, setCodeId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockMinutesRemaining, setLockMinutesRemaining] = useState(0);

  // Ref to track whether the initial code request has already been sent.
  // This prevents React StrictMode's double-mount from firing two requests.
  const hasRequestedCode = useRef(false);
  // Ref to hold the latest codeId so the cleanup closure always has the current value
  const codeIdRef = useRef(null);

  // Keep the ref in sync with state
  useEffect(() => {
    codeIdRef.current = codeId;
  }, [codeId]);

  // Request initial code on mount and cleanup on unmount
  useEffect(() => {
    if (hasRequestedCode.current) return; // Already requested (StrictMode re-mount)
    hasRequestedCode.current = true;

    const abortController = new AbortController();
    requestCode(abortController.signal);
    
    // Cleanup: abort in-flight request and cancel pending code when component unmounts
    return () => {
      abortController.abort();
      const currentCodeId = codeIdRef.current;
      if (currentCodeId && !isLocked) {
        axios.post(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/mfa/cancel-code`,
          { userId, codeId: currentCodeId }
        ).catch(err => {
          console.error('Failed to cancel MFA code on unmount:', err);
        });
      }
    };
  }, []);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const requestCode = async (signal) => {
    setIsRequestingCode(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/mfa/request-code`,
        {
          userId,
          deviceFingerprint,
          triggerReason,
        },
        { signal }
      );

      setCodeId(response.data.codeId);
      setExpiresAt(response.data.expiresAt);
      setResendCooldown(60); // 60 second cooldown
      setIsLocked(false);
      setLockMinutesRemaining(0);
    } catch (err) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.error || 'Failed to send verification code';
      setError(errorMessage);
      
      // If locked, disable everything and show how long to wait
      if (errorData?.locked) {
        setIsLocked(true);
        setLockMinutesRemaining(errorData.minutesRemaining || 0);
        // Don't set isLoading - that shows the spinner and disables cancel
      }
    } finally {
      setIsRequestingCode(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !codeId) return;

    setIsRequestingCode(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/mfa/resend-code`,
        { codeId }
      );

      setResendCooldown(60);
      setError('');
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to resend verification code';
      setError(errorMessage);
    } finally {
      setIsRequestingCode(false);
    }
  };

  // Alias for button compatibility
  const handleResendCode = handleResend;

  const handleVerify = async (codeValue) => {
    // Use passed value or current state
    const verificationCode = codeValue || code;
    
    if (verificationCode.length !== 4) {
      setError('Please enter a 4-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/mfa/verify-code`,
        {
          userId,
          code: verificationCode,
          deviceFingerprint,
        }
      );

      if (response.data.success) {
        onSuccess();
      }
    } catch (err) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.error || 'Invalid verification code';
      setError(errorMessage);
      setCode(''); // Clear the code on error
      
      // If locked, update lock state
      if (errorData?.locked) {
        setIsLocked(true);
        setLockMinutesRemaining(errorData.minutesRemaining || 0);
      }
    } finally {
      // Always set isLoading to false to hide spinner
      setIsLoading(false);
    }
  };

  const handleCodeChange = (value) => {
    setCode(value);
    setError(''); // Clear error when user starts typing
    
    // Auto-submit when 4 digits are entered
    if (value.length === 4) {
      setTimeout(() => handleVerify(value), 100);
    }
  };

  const handleCancel = async () => {
    // Cancel the pending code before closing
    if (codeId) {
      try {
        await axios.post(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/mfa/cancel-code`,
          { userId, codeId }
        );
      } catch (err) {
        console.error('Failed to cancel MFA code:', err);
      }
    }
    onCancel();
  };

  const getReasonMessage = () => {
    const messages = {
      'new_device': 'We detected a login from a new device',
      'new_ip': 'We detected a login from a new IP address',
      'user_switch': 'A different user is logging in on this device',
      'time_elapsed': "It's been a while since your last verification",
      'first_time': 'This is your first login with multi-factor authentication',
    };
    return messages[triggerReason] || 'Security verification required';
  };

  return (
    <div className="mfa-code-entry-overlay">
      <div className="mfa-code-entry-container">
        <div className="mfa-header">
          <h2>🔐 Security Verification</h2>
          <p className="mfa-reason">{reason || getReasonMessage()}</p>
        </div>

        <div className="mfa-body">
          {!isLocked ? (
            <p className="mfa-instruction">
              A verification code has been sent to your email address. 
              Please enter it below to continue.
            </p>
          ) : (
            <p className="mfa-instruction mfa-locked-message">
              Your account is temporarily locked due to too many failed attempts.
              Please wait {lockMinutesRemaining} minute{lockMinutesRemaining !== 1 ? 's' : ''} before trying again.
            </p>
          )}

          <div className="mfa-code-input-wrapper">
            <CodeInput
              length={4}
              value={code}
              onChange={handleCodeChange}
              autoFocus={!isLocked}
              disabled={isLoading || isLocked}
            />
          </div>

          {error && (
            <div className="mfa-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {isLoading && !isLocked && (
            <div className="mfa-loading">
              <div className="spinner"></div>
              <span>Verifying...</span>
            </div>
          )}

          <div className="mfa-actions">
            <button
              className="mfa-resend-button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || isRequestingCode || isLocked}
            >
              {isRequestingCode ? (
                'Sending...'
              ) : resendCooldown > 0 ? (
                `Resend in ${resendCooldown}s`
              ) : isLocked ? (
                `Locked (${lockMinutesRemaining}m remaining)`
              ) : (
                'Resend Code'
              )}
            </button>

            {onCancel && (
              <button
                className="mfa-cancel-button"
                onClick={handleCancel}
                disabled={isLoading && !isLocked}
              >
                Cancel
              </button>
            )}
          </div>

          <div className="mfa-help">
            <p className="help-text">
              <strong>Didn't receive the code?</strong>
            </p>
            <ul>
              <li>Check your spam/junk folder</li>
              <li>Wait a few moments and try resending</li>
              <li>Contact your administrator if issues persist</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaCodeEntry;
